const ATTRIBUTION_STORAGE_KEY = 'finradar.attribution.v1';
const SESSION_ATTRIBUTION_STORAGE_KEY = 'finradar.session_attribution.v1';
const ATTRIBUTION_VERSION = '2026-04-24';
const FIRST_TOUCH_TTL_MS = 365 * 24 * 60 * 60 * 1000;
const MAX_URL_LENGTH = 1500;
const MAX_QUERY_LENGTH = 1000;

const MARKETING_PARAM_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'utm_id',
    'gclid',
    'gbraid',
    'wbraid',
    'fbclid',
    'yclid',
    'ttclid',
    'msclkid',
];

const safeParseJson = (value) => {
    if (!value) return null;

    try {
        return JSON.parse(value);
    } catch (_error) {
        return null;
    }
};

const getStorage = (kind) => {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        return kind === 'session' ? window.sessionStorage : window.localStorage;
    } catch (_error) {
        return null;
    }
};

const readStoredValue = (key, kind = 'local') => {
    const storage = getStorage(kind);
    return storage ? safeParseJson(storage.getItem(key)) : null;
};

const writeStoredValue = (key, value, kind = 'local') => {
    const storage = getStorage(kind);
    if (!storage) return;

    try {
        storage.setItem(key, JSON.stringify(value));
    } catch (_error) {
        try {
            storage.removeItem(key);
            storage.setItem(key, JSON.stringify(value));
        } catch (_nestedError) {
            // Ignore storage quota / privacy mode failures.
        }
    }
};

const removeStoredValue = (key, kind = 'local') => {
    const storage = getStorage(kind);
    if (!storage) return;

    try {
        storage.removeItem(key);
    } catch (_error) {
        // Ignore storage removal failures.
    }
};

const getDeviceType = () => {
    if (typeof window === 'undefined') return null;

    const width = window.innerWidth || 0;
    if (width > 0 && width < 768) return 'mobile';
    if (width > 0 && width < 1200) return 'tablet';
    return 'desktop';
};

const normalizeParamValue = (value) => {
    const normalized = String(value || '').trim();
    return normalized || null;
};

const clampValue = (value, maxLength) => {
    const normalized = normalizeParamValue(value);
    if (!normalized) return null;
    return normalized.slice(0, maxLength);
};

const collectMarketingParams = (searchParams) => MARKETING_PARAM_KEYS.reduce((acc, key) => {
    const value = normalizeParamValue(searchParams.get(key));
    if (value) {
        acc[key] = value;
    }
    return acc;
}, {});

const getReferrerInfo = (currentOrigin) => {
    if (typeof document === 'undefined') {
        return { referrer: null, referrerHost: null, isExternalReferrer: false };
    }

    const referrer = normalizeParamValue(document.referrer);
    if (!referrer) {
        return { referrer: null, referrerHost: null, isExternalReferrer: false };
    }

    try {
        const referrerUrl = new URL(referrer);
        return {
            referrer,
            referrerHost: referrerUrl.hostname || null,
            isExternalReferrer: referrerUrl.origin !== currentOrigin,
        };
    } catch (_error) {
        return {
            referrer,
            referrerHost: null,
            isExternalReferrer: true,
        };
    }
};

const resolveSourceFromTouch = ({ marketingParams, referrerHost, isExternalReferrer }) => {
    if (marketingParams.utm_source) {
        return marketingParams.utm_source;
    }
    if (marketingParams.gclid) {
        return 'google_ads';
    }
    if (marketingParams.yclid) {
        return 'yandex_direct';
    }
    if (marketingParams.fbclid) {
        return 'facebook_ads';
    }
    if (marketingParams.ttclid) {
        return 'tiktok_ads';
    }
    if (marketingParams.msclkid) {
        return 'microsoft_ads';
    }
    if (isExternalReferrer && referrerHost) {
        return referrerHost;
    }
    if (isExternalReferrer) {
        return 'referral';
    }
    return 'direct';
};

const buildWhitelistedQueryString = (marketingParams) => {
    const queryParams = new URLSearchParams();

    Object.entries(marketingParams).forEach(([key, value]) => {
        if (value) {
            queryParams.set(key, value);
        }
    });

    return clampValue(queryParams.toString(), MAX_QUERY_LENGTH);
};

const buildWhitelistedUrl = (url, marketingParams) => {
    const sanitizedUrl = new URL(url.toString());
    sanitizedUrl.search = '';

    Object.entries(marketingParams).forEach(([key, value]) => {
        if (value) {
            sanitizedUrl.searchParams.set(key, value);
        }
    });

    return clampValue(sanitizedUrl.toString(), MAX_URL_LENGTH);
};

const toStoredTouchSnapshot = (touch) => {
    if (!touch) return null;

    return {
        occurredAt: touch.occurredAt,
        url: touch.url,
        path: touch.path,
        query: touch.query,
        source: touch.source,
        referrerHost: touch.referrerHost,
        marketingParams: touch.marketingParams,
        clickIds: touch.clickIds,
        deviceType: touch.deviceType,
    };
};

const createTouchSnapshot = () => {
    if (typeof window === 'undefined') {
        return null;
    }

    const url = new URL(window.location.href);
    const marketingParams = collectMarketingParams(url.searchParams);
    const query = buildWhitelistedQueryString(marketingParams);
    const { referrer, referrerHost, isExternalReferrer } = getReferrerInfo(url.origin);

    return {
        occurredAt: new Date().toISOString(),
        url: buildWhitelistedUrl(url, marketingParams),
        path: url.pathname,
        query,
        source: resolveSourceFromTouch({ marketingParams, referrerHost, isExternalReferrer }),
        referrer,
        referrerHost,
        marketingParams,
        clickIds: {
            gclid: marketingParams.gclid || null,
            gbraid: marketingParams.gbraid || null,
            wbraid: marketingParams.wbraid || null,
            fbclid: marketingParams.fbclid || null,
            yclid: marketingParams.yclid || null,
            ttclid: marketingParams.ttclid || null,
            msclkid: marketingParams.msclkid || null,
        },
        deviceType: getDeviceType(),
    };
};

const isNonDirectSource = (touch) => Boolean(touch?.source && touch.source !== 'direct');

const isExpiredTimestamp = (isoTimestamp, ttlMs) => {
    if (!isoTimestamp) return true;

    const timestamp = Date.parse(isoTimestamp);
    if (Number.isNaN(timestamp)) return true;

    return (Date.now() - timestamp) > ttlMs;
};

const readActiveAttributionState = () => {
    const storedAttribution = readStoredValue(ATTRIBUTION_STORAGE_KEY) || {};

    if (isExpiredTimestamp(storedAttribution?.firstTouch?.occurredAt, FIRST_TOUCH_TTL_MS)) {
        removeStoredValue(ATTRIBUTION_STORAGE_KEY);
        removeStoredValue(SESSION_ATTRIBUTION_STORAGE_KEY, 'session');
        return {};
    }

    return storedAttribution;
};

export const initializeAttributionTracking = () => {
    const currentTouch = createTouchSnapshot();
    if (!currentTouch) {
        return null;
    }

    const storedAttribution = readActiveAttributionState();
    const storedSession = readStoredValue(SESSION_ATTRIBUTION_STORAGE_KEY, 'session');

    const firstTouch = storedAttribution.firstTouch || toStoredTouchSnapshot(currentTouch);
    const isNewSession = !storedSession?.sessionTouch;
    const sessionTouch = storedSession?.sessionTouch || toStoredTouchSnapshot(currentTouch);
    const lastNonDirectTouch = isNonDirectSource(currentTouch)
        ? toStoredTouchSnapshot(currentTouch)
        : (storedAttribution.lastNonDirectTouch || null);

    const updatedAttribution = {
        version: ATTRIBUTION_VERSION,
        firstTouch,
        lastNonDirectTouch,
        visitCount: Number(storedAttribution.visitCount || 0) + (isNewSession ? 1 : 0),
        sessionCount: Number(storedAttribution.sessionCount || 0) + (isNewSession ? 1 : 0),
        updatedAt: currentTouch.occurredAt,
    };

    writeStoredValue(ATTRIBUTION_STORAGE_KEY, updatedAttribution);
    if (isNewSession) {
        writeStoredValue(SESSION_ATTRIBUTION_STORAGE_KEY, {
            version: ATTRIBUTION_VERSION,
            sessionTouch,
            createdAt: currentTouch.occurredAt,
        }, 'session');
    }

    return {
        ...updatedAttribution,
        sessionTouch,
    };
};

export const getAttributionSnapshot = () => {
    const currentTouch = createTouchSnapshot();
    const storedAttribution = readActiveAttributionState();
    const storedSession = readStoredValue(SESSION_ATTRIBUTION_STORAGE_KEY, 'session');

    return {
        version: ATTRIBUTION_VERSION,
        firstTouch: storedAttribution.firstTouch || currentTouch,
        sessionTouch: storedSession?.sessionTouch || currentTouch,
        lastNonDirectTouch: storedAttribution.lastNonDirectTouch || null,
        visitCount: Number(storedAttribution.visitCount || 0),
        sessionCount: Number(storedAttribution.sessionCount || 0),
    };
};

export const buildSignupAttributionPayload = () => {
    const snapshot = getAttributionSnapshot();
    const submittedAt = new Date().toISOString();
    const registrationTouch = createTouchSnapshot();

    const attributionPayload = {
        version: snapshot.version,
        submittedAt,
        visitCount: snapshot.visitCount,
        sessionCount: snapshot.sessionCount,
        firstTouch: snapshot.firstTouch,
        sessionTouch: snapshot.sessionTouch,
        lastNonDirectTouch: snapshot.lastNonDirectTouch,
        registrationTouch,
    };

    return {
        firstTouchAt: snapshot.firstTouch?.occurredAt || null,
        firstTouchSource: snapshot.firstTouch?.source || null,
        sessionSource: snapshot.sessionTouch?.source || null,
        landingUrl: registrationTouch?.url || null,
        landingQuery: registrationTouch?.query || null,
        sessionStartedAt: snapshot.sessionTouch?.occurredAt || null,
        sessionLandingUrl: snapshot.sessionTouch?.url || null,
        sessionLandingQuery: snapshot.sessionTouch?.query || null,
        lastNonDirectTouchAt: snapshot.lastNonDirectTouch?.occurredAt || null,
        lastNonDirectTouchSource: snapshot.lastNonDirectTouch?.source || null,
        attributionVersion: snapshot.version,
        visitCount: snapshot.visitCount || null,
        sessionCount: snapshot.sessionCount || null,
        attributionJson: JSON.stringify(attributionPayload),
    };
};
